// CustomMarkdownEditor.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import CodeIcon from '@mui/icons-material/Code';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import UndoIcon from '@mui/icons-material/Undo';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Picker from 'emoji-picker-react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { TypeAnimation } from 'react-type-animation'; // Import TypeAnimation

import BusinessIcon from '@mui/icons-material/Business';
import RemoveIcon from '@mui/icons-material/Remove';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import ShortTextIcon from '@mui/icons-material/ShortText';
import SubjectIcon from '@mui/icons-material/Subject';
import TranslateIcon from '@mui/icons-material/Translate';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { GENAI_ENHANCE_TEXT_ENDPOINT } from '../../config/urls';

const CustomMarkdownEditor = ({ value, onChange, maxLength, disabled=false, onKeyDown=() => {}, id }) => {
    const [text, setText] = useState(value || '');
    const [history, setHistory] = useState([value || '']);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const textareaRef = useRef(null);
    // CHANGE: Added state to track if the last key pressed was Enter for double-Enter detection
    const [lastKeyWasEnter, setLastKeyWasEnter] = useState(false);

    // First, add these to your state variables
    const [enhancedText, setEnhancedText] = useState('');
    const [isEnhancing, setIsEnhancing] = useState(false);
    const [showEnhanced, setShowEnhanced] = useState(false);
    const [enhanceOption, setEnhanceOption] = useState('professional');
    const [enhanceError, setEnhanceError] = useState('');
    const [animationComplete, setAnimationComplete] = useState(false);
    const permissionsFromStorage = JSON.parse(localStorage.getItem('permissions') || '[]');
    const currentPlan = (localStorage.getItem('plan') || '').toLowerCase();
    const genAIEnabledPlans = ['automate', 'orchestrate'];
    // Define the specific permission required for text enhancement
    const ENHANCE_PERMISSION = 'gen_ai_live_agent.change';

    // Add this menu state for the enhance options
    const [enhanceMenuAnchor, setEnhanceMenuAnchor] = useState(null);
    
    const isGenAIEnabledByPlan = genAIEnabledPlans.includes(currentPlan);
    const hasEnhancePermission =
      permissionsFromStorage.includes(ENHANCE_PERMISSION) || isGenAIEnabledByPlan;

    // Define consistent text styling for all text areas
    const textAreaStyle = {
        width: '100%',
        height: '150px',
        padding: '12px',
        fontFamily: 'monospace',
        fontSize: '14px', 
        border: '1px solid #ddd',
        resize: 'vertical',
        outline: 'none',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.5', 
        borderRadius: "8px"
    };

    // Add this function to cancel enhancement
    const handleCancelEnhancement = () => {
        setShowEnhanced(false);
        setEnhancedText('');
        setAnimationComplete(false);
    };
    
    // Add this function to replace the original text with enhanced text
    const handleReplaceWithEnhanced = (maxLength) => {
        // Save current text to history for undo
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(text);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    
        // Limit enhancedText to 1020 characters
        const limitedText = enhancedText.slice(0, maxLength);
    
        // Replace text with enhanced version
        setText(limitedText);
        onChange(limitedText);
    
        // Close enhanced preview
        setShowEnhanced(false);
        setEnhancedText('');
        setAnimationComplete(false);
    };
    
    // Add this function to close the enhance menu
    const handleCloseEnhanceMenu = () => {
        setEnhanceMenuAnchor(null);
    };

    // Add this function to select an enhancement option
    const handleSelectEnhanceOption = (option) => {
        setEnhanceOption(option);
        handleCloseEnhanceMenu();
        performEnhancement(option);
    };

    // Add this function to perform the text enhancement
    const performEnhancement = async (option) => {
        if (!text.trim()) return;
        
        setIsEnhancing(true);
        setEnhanceError('');
        setAnimationComplete(false);
        
        try {
            const response = await axios.post(`${GENAI_ENHANCE_TEXT_ENDPOINT}`, {
                prompt: text,
                tone: option
            }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            setEnhancedText(response.data.enhanced_text);
            setShowEnhanced(true);
        } catch (error) {
            console.error('Error enhancing text:', error);
            setEnhanceError(error.response?.data?.error || 'Failed to enhance text');
        } finally {
            setIsEnhancing(false);
        }
    };

    // Handle animation completion
    const handleAnimationComplete = () => {
        setAnimationComplete(true);
    };
    
    const handleKeyDownInternal = (e) => {
        if (!textareaRef.current) return;
        
        const textarea = textareaRef.current;
        const cursorPos = textarea.selectionStart;
        const lineInfo = getLineInfo(text, cursorPos);
        
        // Handle Enter key in list contexts
        // if (e.key === 'Enter') {
        //     if (lineInfo.type === 'bullet' || lineInfo.type === 'number') {
        //         e.preventDefault();
                
        //         // CHANGE: Simplified double-Enter detection - exit list if last key was also Enter
        //         if (lastKeyWasEnter || lineInfo.isEmptyItem) {
        //             // Remove the empty list item and exit list mode
        //             const newText = text.substring(0, lineInfo.lineStart) + 
        //                         text.substring(lineInfo.lineEnd);
        //             updateTextWithHistory(newText);
                    
        //             // Position cursor at the beginning of the now-empty line
        //             setTimeout(() => {
        //                 textarea.setSelectionRange(lineInfo.lineStart, lineInfo.lineStart);
        //             }, 0);
                    
        //             // Reset the Enter tracking
        //             setLastKeyWasEnter(false);
        //             return;
        //         }
                
        //         // Create new list item (single Enter)
        //         let newListItem = '';
        //         if (lineInfo.type === 'bullet') {
        //             newListItem = `\n${lineInfo.indent}${lineInfo.marker} `;
        //         } else if (lineInfo.type === 'number') {
        //             const nextNum = getNextNumber(text, cursorPos, lineInfo.indent);
        //             newListItem = `\n${lineInfo.indent}${nextNum}. `;
        //         }
                
        //         const newText = text.substring(0, cursorPos) + 
        //                     newListItem + 
        //                     text.substring(cursorPos);
                
        //         if (newText.length <= maxLength) {
        //             updateTextWithHistory(newText);
                    
        //             // Position cursor after the new list marker
        //             setTimeout(() => {
        //                 const newPos = cursorPos + newListItem.length;
        //                 textarea.setSelectionRange(newPos, newPos);
        //             }, 0);
        //         }
                
        //         // CHANGE: Track that Enter was pressed for double-Enter detection
        //         setLastKeyWasEnter(true);
        //         return;
        //     }
        // }
        if (e.key === 'Enter') {
    if (lineInfo.type === 'bullet' || lineInfo.type === 'number') {
        e.preventDefault();

        // Exit case: only if current line is empty AND last key was Enter
        if (lineInfo.isEmptyItem && lastKeyWasEnter) {
            const newText = text.substring(0, lineInfo.lineStart) + 
                            text.substring(lineInfo.lineEnd);
            updateTextWithHistory(newText);

            setTimeout(() => {
                textarea.setSelectionRange(lineInfo.lineStart, lineInfo.lineStart);
            }, 0);

            setLastKeyWasEnter(false);
            return;
        }

        // Insert new list item
        let newListItem = '';
        if (lineInfo.type === 'bullet') {
            newListItem = `\n${lineInfo.indent}${lineInfo.marker} `;
        } else if (lineInfo.type === 'number') {
            const nextNum = getNextNumber(text, cursorPos, lineInfo.indent);
            newListItem = `\n${lineInfo.indent}${nextNum}. `;
        }

        const newText = text.substring(0, cursorPos) +
                        newListItem +
                        text.substring(cursorPos);

        if (newText.length <= maxLength) {
            updateTextWithHistory(newText);
            setTimeout(() => {
                const newPos = cursorPos + newListItem.length;
                textarea.setSelectionRange(newPos, newPos);
            }, 0);
        }

        // ✅ Track Enter only if line is empty (for double-Enter exit)
        setLastKeyWasEnter(lineInfo.isEmptyItem);
        return;
    }
}


        
        // CHANGE: Reset Enter tracking for any non-Enter key
        if (e.key !== 'Enter') {
            setLastKeyWasEnter(false);
        }
        
        // Handle Backspace at the beginning of list items
        if (e.key === 'Backspace') {
            if ((lineInfo.type === 'bullet' || lineInfo.type === 'number') && 
                cursorPos === lineInfo.lineStart + lineInfo.indent.length + 
                (lineInfo.type === 'bullet' ? 2 : lineInfo.number.toString().length + 2)) {
                
                e.preventDefault();
                
                // Remove the list marker, keeping the text
                const beforeMarker = text.substring(0, lineInfo.lineStart);
                const afterMarker = text.substring(cursorPos);
                const listContent = text.substring(cursorPos);
                
                const newText = beforeMarker + lineInfo.indent + listContent;
                updateTextWithHistory(newText);
                
                // Position cursor where the list marker was
                setTimeout(() => {
                    textarea.setSelectionRange(lineInfo.lineStart + lineInfo.indent.length, 
                                            lineInfo.lineStart + lineInfo.indent.length);
                }, 0);
                return;
            }
        }
        
        // Check for Ctrl+Z (Undo)
        if (e.ctrlKey && e.key === 'z') {
            e.preventDefault();
            handleUndo();
            return;
        }
        
        // Pass other keys to parent handler
        if (onKeyDown) {
            onKeyDown(e);
        }
    };

    useEffect(() => {
        // Function to handle keyboard shortcuts
        const handleKeyDown = (e) => {
            // Check for Ctrl+Z (Undo)
            if (e.ctrlKey && e.key === 'z') {
                e.preventDefault(); // Prevent the browser's default undo behavior
                handleUndo();
            }
        };
    
        // Add event listener to the textarea
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.addEventListener('keydown', handleKeyDown);
        }
    
        // Cleanup: remove event listener when component unmounts
        return () => {
            if (textarea) {
                textarea.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [historyIndex]); // Dependency on historyIndex ensures the latest state is used

    useEffect(() => {
        // This effect syncs the component's internal state with the parent's value prop
        if (value !== text) {
            setText(value);
            
            // Update history if this is a genuine update (e.g., clearing the field)
            if (!history.includes(value)) {
                const newHistory = [...history, value];
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
            }
        }
    }, [value]);

    const RichTextArea = ({ value, onChange, maxLength, textareaRef }) => {
        // Function to convert markdown to HTML for display
        const convertMarkdownToDisplayHtml = (content) => {
            if (!content) return '';
            
            let html = content;
            
            // We'll preserve the markdown marks but add styling
            // Bold
            html = html.replace(/\*([^*]+)\*/g, '<span class="markdown-bold">*$1*</span>');
            
            // Italic
            html = html.replace(/_([^_]+)_/g, '<span class="markdown-italic">_$1_</span>');
            
            // Strikethrough
            html = html.replace(/~([^~]+)~/g, '<span class="markdown-strikethrough">~$1~</span>');
            
            // Monospace
            html = html.replace(/```([^`]+)```/g, '<span class="markdown-mono">```$1```</span>');
            html = html.replace(/`([^`]+)`/g, '<span class="markdown-inline-code">`$1`</span>');
            
            // Lists
            html = html.replace(/^(\* .+)$/gm, '<span class="markdown-list-item">$&</span>');
            html = html.replace(/^(- .+)$/gm, '<span class="markdown-list-item">$&</span>');
            html = html.replace(/^(\d+\. .+)$/gm, '<span class="markdown-list-item">$&</span>');
            
            // Quotes
            // html = html.replace(/^(> .+)$/gm, '<span class="markdown-quote">$&</span>');
            html = html.replace(/^(> .*)$/gm, '<div class="markdown-quote">$1</div>');
            
            return html;
        };

        // Style definitions
        const styles = `
            .markdown-bold { font-weight: bold; color: #000; }
            .markdown-italic { font-style: italic; color: #000; }
            .markdown-strikethrough { text-decoration: line-through; color: #000; }
            .markdown-mono, .markdown-inline-code { font-family: monospace; background-color: #f0f0f0; padding: 0 2px; color: #000; }
            .markdown-list-item { display: block; color: #000; }
            .markdown-quote { display: block; border-left: 3px solid #ccc; padding-left: 10px; color: #666; }
        `;
        // .markdown-mono, .markdown-inline-code { font-family: monospace; background-color: #f0f0f0; padding: 0 2px; color: #000; }
        // .markdown-quote { display: block; border-left: 3px solid #ccc; padding-left: 10px; color: #666; }


        // The editor container needs to handle input and maintain selection
        return (
            <div className="rich-text-editor-container" style={{ position: 'relative' }}>
            <style>{styles}</style>
            <div
                className="rich-text-display"
                style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                padding: '12px',
                fontFamily: 'monospace',
                fontSize: '14px',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                pointerEvents: 'none',
                backgroundColor: 'transparent',
                zIndex: 1
                }}
                dangerouslySetInnerHTML={{ __html: convertMarkdownToDisplayHtml(value) }}
            />
            <textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                style={{
                ...textAreaStyle,
                color: 'transparent',
                caretColor: 'black',
                backgroundColor: 'transparent',
                position: 'relative',
                zIndex: 2
                }}
                spellCheck={true}
                maxLength={maxLength}
            />
            </div>
        );
    };

    // Helper function to detect if cursor is at the beginning of a list item
    const getLineInfo = (text, cursorPos) => {
        const lines = text.substring(0, cursorPos).split('\n');
        const currentLine = lines[lines.length - 1];
        const lineStart = cursorPos - currentLine.length;
        
        // Check if current line is a bullet list item
        const bulletMatch = currentLine.match(/^(\s*)([-*])\s/);
        if (bulletMatch) {
            return {
                type: 'bullet',
                indent: bulletMatch[1],
                marker: bulletMatch[2],
                lineStart,
                lineEnd: lineStart + currentLine.length,
                isEmptyItem: currentLine.trim() === '-' || currentLine.trim() === '*' || currentLine.trim() === '- ' || currentLine.trim() === '* '
            };
        }
        
        // Check if current line is a numbered list item
        const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s/);
        if (numberMatch) {
            return {
                type: 'number',
                indent: numberMatch[1],
                number: parseInt(numberMatch[2]),
                lineStart,
                lineEnd: lineStart + currentLine.length,
                isEmptyItem: currentLine.trim().match(/^\d+\.\s*$/)
            };
        }
        
        return {
            type: 'none',
            lineStart,
            lineEnd: lineStart + currentLine.length
        };
    };

    // Helper function to get the next number for numbered lists
    const getNextNumber = (text, cursorPos, indent) => {
        const lines = text.split('\n');
        const currentLineIndex = text.substring(0, cursorPos).split('\n').length - 1;
        
        let nextNumber = 1;
        
        // Look backwards from current line to find the last numbered item with same indent
        for (let i = currentLineIndex; i >= 0; i--) {
            const line = lines[i];
            const match = line.match(/^(\s*)(\d+)\.\s/);
            if (match && match[1] === indent) {
                nextNumber = parseInt(match[2]) + 1;
                break;
            }
        }
        
        return nextNumber;
    };

    // Helper function to update text with history
    const updateTextWithHistory = (newText) => {
        setText(newText);
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newText);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
        onChange(newText);
    };

    // Handle text changes
    // const handleChange = (e) => {
    //     const newText = e.target.value;
        
    //     if (newText.length <= maxLength) {
    //         setText(newText);
            
    //         // Add to history for undo functionality
    //         if (newText !== history[historyIndex]) {
    //             const newHistory = history.slice(0, historyIndex + 1);
    //             newHistory.push(newText);
    //             setHistory(newHistory);
    //             setHistoryIndex(newHistory.length - 1);
    //         }
            
    //         // Call the parent onChange
    //         onChange(newText);
    //     }
    // };

    const handleChange = (e) => {
        let newText = e.target.value;
        
        if (maxLength && newText.length > maxLength) {
            newText = newText.slice(0, maxLength); // Limit text to maxLength
        }
            setText(newText);
            
            // Add to history for undo functionality
            if (newText !== history[historyIndex]) {
                const newHistory = history.slice(0, historyIndex + 1);
                newHistory.push(newText);
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
            }
            
            // Call the parent onChange
            onChange(newText);
    };

    // Handle formatting button clicks
    const applyFormatting = (type) => {
        if (!textareaRef.current) return;
        
        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = text.substring(start, end);
        
        let formattedText = '';
        let newCursorPos = 0;
        let cursorAdjustment = 0;

        // Inside applyFormatting function, replace the bullet-list case:
        if (type === "bullet-list") {
            // CHANGE: Fixed inconsistent behavior - now handles both cases similarly
            if (selectedText === "") {
                // No text selected, create a new bullet point
                const lineInfo = getLineInfo(text, start);
                if (lineInfo.type === "bullet") {
                    // Already in bullet list, do nothing
                    return;
                }

                // CHANGE: Consistent behavior - always add bullet on new line when cursor is at end of existing text
                const lines = text.substring(0, start).split("\n");
                const currentLine = lines[lines.length - 1];
                const lineStartPos = start - currentLine.length;

                // If we're at the start of an empty line, add bullet directly
                if (currentLine.trim() === "" && start === lineStartPos) {
                    formattedText = "- ";
                    newCursorPos = start + 2;
                } else {
                    // Insert bullet on new line (consistent with numbered list)
                    formattedText = "\n- ";
                    newCursorPos = start + 3;
                }

                const newText =
                    text.substring(0, start) + formattedText + text.substring(start);
                if (newText.length <= maxLength) {
                    updateTextWithHistory(newText);
                    setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(newCursorPos, newCursorPos);
                    }, 0);
                }
                return;
            } else {
                const allBulleted = selectedText
                    .split("\n")
                    .every((line) => line.trim() === "" || /^- /.test(line.trim()));

                if (allBulleted) {
                    // Remove bullet formatting
                    formattedText = selectedText
                        .split("\n")
                        .map((line) => line.replace(/^- /, ""))
                        .join("\n");
                } else {
                    // Add bullet formatting
                    formattedText = selectedText
                        .split("\n")
                        .map((line) => (line.trim() ? `- ${line.trim()}` : line))
                        .join("\n");
                }

                const newText =
                    text.substring(0, start) + formattedText + text.substring(end);
                if (newText.length <= maxLength) {
                    setText(newText);
                    const newHistory = history.slice(0, historyIndex + 1);
                    newHistory.push(newText);
                    setHistory(newHistory);
                    setHistoryIndex(newHistory.length - 1);
                    onChange(newText);

                    setTimeout(() => {
                        textarea.focus();
                        const newEnd = start + formattedText.length;
                        textarea.setSelectionRange(start, newEnd);
                    }, 0);
                }
                return;
            }
        }

        if (type === "number-list") {
            // CHANGE: Fixed inconsistent behavior for numbered lists
            if (selectedText === "") {
                // No text selected
                const lineInfo = getLineInfo(text, start);
                
                if (lineInfo.type === "number") {
                    // Already in numbered list, do nothing
                    return;
                }

                // CHANGE: Consistent behavior - always add numbered item on new line when cursor is at end of existing text
                const lines = text.substring(0, start).split("\n");
                const currentLine = lines[lines.length - 1];
                const lineStartPos = start - currentLine.length;

                // If we're at the start of an empty line, add number directly
                if (currentLine.trim() === "" && start === lineStartPos) {
                    formattedText = "1. ";
                    newCursorPos = start + 3;
                } else {
                    // Insert number on new line (now consistent with bullet list)
                    formattedText = "\n1. ";
                    newCursorPos = start + 4;
                }

                const newText =
                    text.substring(0, start) + formattedText + text.substring(start);
                if (newText.length <= maxLength) {
                    updateTextWithHistory(newText);
                    setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(newCursorPos, newCursorPos);
                    }, 0);
                }
                return;
            } else {
                // Toggle numbering for multiple selected lines
                const lines = selectedText.split("\n");

                // Check if all selected lines are already numbered
                const allNumbered = lines.every((line) => /^\s*\d+\.\s/.test(line));

                const formattedText = allNumbered
                    ? // Remove numbering if all lines are already numbered
                      lines.map((line) => line.replace(/^\s*\d+\.\s/, "")).join("\n")
                    : // Add numbering incrementally if not
                      lines
                          .map((line, i) =>
                              line.trim() ? `${i + 1}. ${line.trim()}` : line
                          )
                          .join("\n");

                const newText =
                    text.substring(0, start) + formattedText + text.substring(end);
                updateTextWithHistory(newText);

                setTimeout(() => {
                    textarea.focus();
                    const newEnd = start + formattedText.length;
                    textarea.setSelectionRange(start, newEnd);
                }, 0);
            }
            return;
        }
        // FIXED: Handle quotes similar to lists - moved outside the else block
        else if (type === 'quote') {
            const isAlreadyQuoted = selectedText !== '' && selectedText.split('\n').every(line =>
                line.trim() === '' || line.trim().startsWith('> ')
            );
            
            if (selectedText === '') {
                // No text selected, add quote to current line or create new quoted line
                const lines = text.substring(0, start).split('\n');
                const currentLine = lines[lines.length - 1];
                const lineStartPos = start - currentLine.length;
                
                // If we're at the start of a line or the line is empty, add quote
                if (currentLine.trim() === '' || start === lineStartPos) {
                    formattedText = '> ';
                    newCursorPos = start + 2;
                } else {
                    // Insert quote on new line
                    formattedText = '\n> ';
                    newCursorPos = start + 3;
                }
                
                const newText = text.substring(0, start) + formattedText + text.substring(start);
                if (newText.length <= maxLength) {
                    updateTextWithHistory(newText);
                    setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(newCursorPos, newCursorPos);
                    }, 0);
                }
                return;
            } else {
                // FIXED: Handle selected text for quotes - both add and remove
                if (isAlreadyQuoted) {
                    // Remove quote formatting
                    formattedText = selectedText
                        .split('\n')
                        .map(line => line.replace(/^> /, ''))
                        .join('\n');
                } else {
                    // Add quote formatting
                    formattedText = selectedText
                        .split('\n')
                        .map(line => line.trim() ? `> ${line}` : line)
                        .join('\n');
                }
                
                // Calculate the new text and apply it
                const newText = text.substring(0, start) + formattedText + text.substring(end);
                
                if (newText.length <= maxLength) {
                    setText(newText);
                    
                    // Add to history
                    const newHistory = history.slice(0, historyIndex + 1);
                    newHistory.push(newText);
                    setHistory(newHistory);
                    setHistoryIndex(newHistory.length - 1);
                    
                    // Call the parent onChange
                    onChange(newText);
                    
                    // Set focus back to the textarea and select the formatted text
                    setTimeout(() => {
                        textarea.focus();
                        const newEnd = start + formattedText.length;
                        textarea.setSelectionRange(start, newEnd);
                    }, 0);
                }
                return; // FIXED: Important - return here to avoid further processing
            }
        } 
        else {
        // Helper function to check if text already has formatting
        const hasFormatting = (text, format) => {
            switch(format) {
                case 'bold':
                    return text.startsWith('*') && text.endsWith('*');
                case 'italic':
                    return text.startsWith('_') && text.endsWith('_');
                case 'strikethrough':
                    return text.startsWith('~') && text.endsWith('~');
                case 'monospace':
                    return text.startsWith('```') && text.endsWith('```');
                case 'inline-code':
                    return text.startsWith('`') && text.endsWith('`');
                // case 'quote':
                //     // return text.split('\n').every(line => line.trim() === '' || line.trim().startsWith('> '));
                //     return text.split('\n').every(line =>
                //         line.trim() === '' || line.trim().startsWith('> ')
                //     );
                default:
                    return false;
            }
        };
        
        // Check if the selected text already has the formatting we're trying to apply
        const alreadyFormatted = hasFormatting(selectedText, type);
        
        if (alreadyFormatted) {
            // Remove formatting
            switch (type) {
                case 'bold':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed asterisks
                    break;
                case 'italic':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed underscores
                    break;
                case 'strikethrough':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed tildes
                    break;
                case 'monospace':
                    formattedText = selectedText.substring(3, selectedText.length - 3);
                    cursorAdjustment = -6; // Adjust cursor for removed backticks
                    break;
                case 'inline-code':
                    formattedText = selectedText.substring(1, selectedText.length - 1);
                    cursorAdjustment = -2; // Adjust cursor for removed backticks
                    break;
                // case 'quote': // FIXED: Added quote removal logic
                //     formattedText = selectedText
                //         .split('\n')
                //         .map(line => line.replace(/^> /, ''))
                //         .join('\n');
                //     break;
                default:
                    formattedText = selectedText;
            }
        } else {
            // Add formatting
            switch (type) {
                case 'bold':
                    formattedText = `*${selectedText}*`;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added asterisks
                    break;
                case 'italic':
                    formattedText = `_${selectedText}_`;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added underscores
                    break;
                case 'strikethrough':
                    formattedText = `~${selectedText}~`;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added tildes
                    break;
                case 'monospace':
                    formattedText = `\`\`\`${selectedText}\`\`\``;
                    newCursorPos = 3;
                    cursorAdjustment = 6; // Adjust cursor for added backticks
                    break;
                case 'inline-code':
                    formattedText = `\`${selectedText}\``;
                    newCursorPos = 1;
                    cursorAdjustment = 2; // Adjust cursor for added backticks
                    break;
                default:
                    formattedText = selectedText;
            }
        }
        
        // Calculate the new text length and check against maxLength
        const newText = text.substring(0, start) + formattedText + text.substring(end);
        
        // ** NEW: Check if the formatted text exceeds maxLength **
        if (newText.length > maxLength) {
            console.warn('Formatting would exceed maximum length limit');
            return; // Prevent the formatting if it would exceed the limit
        }
        
        setText(newText);
        
        // Add to history
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newText);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
        
        // Call the parent onChange
        onChange(newText);
        
        // Set focus back to the textarea and position cursor
        setTimeout(() => {
        textarea.focus();
        if (type === 'bullet-list' || type === 'number-list') {
            // For lists, position cursor at the end of formatted text
            const newPos = start + formattedText.length;
            textarea.setSelectionRange(newPos, newPos);
        } else if (start === end) {
            // If no text was selected, position cursor between markers
            const newPos = start + newCursorPos;
            textarea.setSelectionRange(newPos, newPos);
        } else {
            // If text was selected, select the newly formatted text
            const newStart = start;
            const newEnd = start + formattedText.length;
            textarea.setSelectionRange(newStart, newEnd);
        }
        }, 0);
    };
    };

    // Handle undo
    const handleUndo = () => {
        if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
            const previousText = history[historyIndex - 1];
            setText(previousText);
            onChange(previousText);
        }
    };

    // Handle enhance click
    const handleClickEnhance = (event) => {
        if (!hasEnhancePermission) {
          return; // Do nothing if no permission
        }
        setEnhanceMenuAnchor(event.currentTarget);
    };

    // Handle emoji selection
    const onEmojiClick = (emojiData, event) => {
        // The API of emoji-picker-react has changed, emoji data is now the first parameter
        // Check if we have valid emoji data
        if (!emojiData || !emojiData.emoji) {
            console.error("Invalid emoji data:", emojiData);
            return;
        }
        
        const emoji = emojiData.emoji;
        const textarea = textareaRef.current;
        
        if (!textarea) {
            console.error("Textarea reference is undefined");
            return;
        }
        
        const start = textarea.selectionStart;
        
        const newText = 
            text.substring(0, start) + 
            emoji + 
            text.substring(start);
        
        // Check if newText is within maxLength before updating state
        if (maxLength && newText.length > maxLength) {
            console.warn("Adding emoji would exceed maxLength");
            return;
        }
        
        setText(newText);
        
        // Add to history
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newText);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
        
        // Call the parent onChange
        onChange(newText);
        
        // Close emoji picker
        setShowEmojiPicker(false);
        
        // Set focus back to textarea
        setTimeout(() => {
            textarea.focus();
            const newPos = start + emoji.length;
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    const iconSize = "small";

    return (
    <div style={{ width: '100%'}}>
        {/* Editor and Preview Layout */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginLeft: 'auto', marginRight: "2px", marginBottom: "2px" }}>
                <Typography variant="body2" color={text.length > maxLength * 0.9 ? 'error' : 'textSecondary'}>
                    {text.length}/{maxLength}
                </Typography>
            </div>
            {/* Text Editor */}
            <textarea
                id={id}
                ref={textareaRef}
                value={text}
                onChange={handleChange}
                onKeyDown={handleKeyDownInternal}
                style={textAreaStyle}
                spellCheck={true}
                disabled={disabled}
            />
        </div>
    
        {/* Toolbar */}
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: "flex-end",
            padding: '4px 2px',
            overflowX: 'auto',
            pointerEvents: disabled ? "none": "all"
        }}>
            <Tooltip title="Bold (*text*)">
                <IconButton onClick={() => applyFormatting('bold')}>
                    <FormatBoldIcon fontSize={iconSize} />
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Italic (_text_)">
                <IconButton onClick={() => applyFormatting('italic')}>
                    <FormatItalicIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Strikethrough (~text~)">
                <IconButton onClick={() => applyFormatting('strikethrough')}>
                    <FormatStrikethroughIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Monospace (```text```)">
                <IconButton onClick={() => applyFormatting('monospace')}>
                    <CodeIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
            
            <Divider orientation="vertical" flexItem style={{ margin: '6px 8px' }} />
            
            <Tooltip title="Bulleted List (* text)">
                <IconButton onClick={() => applyFormatting('bullet-list')}>
                    <FormatListBulletedIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Numbered List (1. text)">
                <IconButton onClick={() => applyFormatting('number-list')}>
                    <FormatListNumberedIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Quote (> text)">
                <IconButton onClick={() => applyFormatting('quote')}>
                    <FormatQuoteIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Inline Code (`text`)">
                <IconButton onClick={() => applyFormatting('inline-code')}>
                    <CodeIcon fontSize={iconSize} />
                </IconButton>
            </Tooltip>
            
            <Divider orientation="vertical" flexItem style={{ margin: '6px 8px' }} />
            
            <Tooltip title="Undo">
                <IconButton 
                    onClick={handleUndo}
                    disabled={historyIndex === 0}
                >
                    <UndoIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
            
            <Tooltip title="Emoji">
                <IconButton onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                    <EmojiEmotionsIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>

            <Tooltip 
                title={
                !hasEnhancePermission
                    ? "Feature is not available in your current plan"
                    : "Enhance Text"
                }
            >
                <IconButton 
                    onClick={handleClickEnhance}
                    disabled={!value}
                >
                    <AutoFixHighIcon fontSize={iconSize}/>
                </IconButton>
            </Tooltip>
        </div>

        {/* Enhanced Text with Animation */}
        {showEnhanced && (
            <div style={{
                marginTop: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
            }}>
                <div style={{
                    padding: '8px',
                    backgroundColor: '#f0f7ff',
                    borderBottom: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <AutoFixHighIcon fontSize="small" style={{ marginRight: '8px', color: '#1976d2' }} />
                    <Typography variant="subtitle2">Enhanced Text ({enhanceOption})</Typography>
                </div>
                
                {/* Typing Animation container */}
                <div style={{
                    width: '100%',
                    height: '120px',
                    padding: '12px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    backgroundColor: '#fafafa',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap'
                }}>
                    {isEnhancing ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={30} />
                        </div>
                    ) : (
                        <TypeAnimation
                            splitter={(str) => str.split(/(?= )/)} // Split by spaces but keep spaces with the following word
                            sequence={[
                                enhancedText,
                                1000, // Wait 1 second after typing
                                () => handleAnimationComplete(), // Callback when animation is complete
                            ]}
                            speed={{ type: 'keyStrokeDelayInMs', value: 30 }}
                            omitDeletionAnimation={true}
                            style={{
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                display: 'block',
                                minHeight: '100px',
                                lineHeight: '1.5',
                                whiteSpace: 'pre-wrap',
                                color: 'inherit'
                            }}
                        />
                    )}
                </div>
                
                <div style={{
                    padding: '8px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #ddd',
                    backgroundColor: '#f5f5f5'
                }}>
                    <Button 
                        variant="text"
                        onClick={handleCancelEnhancement}
                        style={{ marginRight: '8px' }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={() => handleReplaceWithEnhanced(maxLength - 4)}
                        disabled={isEnhancing || !animationComplete} // Disable until animation completes
                    >
                        Replace
                    </Button>
                </div>
            </div>
        )}
    
        {/* Enhancement Options Menu */}
        <Menu
            anchorEl={enhanceMenuAnchor}
            open={Boolean(enhanceMenuAnchor)}
            onClose={handleCloseEnhanceMenu}
        >
            <MenuItem onClick={() => handleSelectEnhanceOption('professional')}>
                <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Professional" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('neutral')}>
                <ListItemIcon><RemoveIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Neutral" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('friendly')}>
                <ListItemIcon><SentimentSatisfiedAltIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Friendly" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('humorous')}>
                <ListItemIcon><EmojiEmotionsIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Humorous" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleSelectEnhanceOption('grammar_check')}>
                <ListItemIcon><SpellcheckIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Grammar & Spell Check" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleSelectEnhanceOption('shorten')}>
                <ListItemIcon><ShortTextIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Make it Short" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('lengthen')}>
                <ListItemIcon><SubjectIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Make it Longer" />
            </MenuItem>
            <MenuItem onClick={() => handleSelectEnhanceOption('simplify')}>
                <ListItemIcon><TranslateIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Make it Simple" />
            </MenuItem>
        </Menu>
        
        {/* Emoji Picker */}
        {showEmojiPicker && (
            <div style={{ 
                position: 'absolute',
                zIndex: 1000,
                marginTop: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                border: '1px solid #ddd',
                borderRadius: '4px'
            }}>
                <Picker 
                    onEmojiClick={onEmojiClick}
                    lazyLoadEmojis={true}
                />
            </div>
        )}
        </div>
    );
}; 

export default CustomMarkdownEditor;